import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Readiness snapshot trend surface (T-0088 / E08-F06).
 *
 * Two read paths from the backend:
 *   - GET /api/v1/engagements/:id/readiness/snapshots  — paginated list
 *     (newest-first), used by the snapshot-history modal trigger.
 *   - GET /api/v1/engagements/:id/readiness/trend       — ASC time series
 *     (chart-friendly), used by the trend screen sparkline.
 *
 * Each trend point carries the denormalized scorecard summary (total %,
 * recommendation, scored count). The trend screen uses these to plot a
 * 0–100 sparkline with go/conditional/no-go gate bands and pin points
 * that match the prototype's `readinessSnapTrend()` chart.
 */

export const ReadinessTrendPointSchema = z.object({
  captured_at: z.string(),
  total_percent: z.number().nullable(),
  recommendation: z.string().nullable(),
  scored_count: z.number(),
  criteria_count: z.number(),
  // T-0240: the snapshot and who captured it (optional for older APIs).
  snapshot_id: z.string().optional(),
  captured_by_name: z.string().nullable().optional(),
});
export type ReadinessTrendPoint = z.infer<typeof ReadinessTrendPointSchema>;

export const ReadinessTrendResultSchema = z.object({
  points: z.array(ReadinessTrendPointSchema),
  range: z.object({
    from: z.string().nullable(),
    to: z.string().nullable(),
  }),
});
export type ReadinessTrendResult = z.infer<typeof ReadinessTrendResultSchema>;

export interface GetReadinessTrendOpts {
  from?: string;
  to?: string;
  limit?: number;
}

export function getReadinessTrend(
  engagementId: string,
  opts: GetReadinessTrendOpts = {},
): Promise<ReadinessTrendResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/readiness/trend`,
    ReadinessTrendResultSchema,
    { query: opts as Record<string, string | number | boolean | undefined> },
  );
}

export function useReadinessTrendQuery(
  engagementId: string | undefined,
  opts: GetReadinessTrendOpts = {},
) {
  return useQuery({
    queryKey: ['readiness', 'trend', engagementId, opts] as const,
    queryFn: () => getReadinessTrend(engagementId!, opts),
    enabled: Boolean(engagementId),
    staleTime: 30_000,
  });
}

// ===========================================================================
// Readiness snapshots list — newest-first, used by the trend "snapshot pin"
// callouts and the snapshot-history modal trigger.
// ===========================================================================

export const ReadinessSnapshotListItemSchema = z.object({
  snapshot_id: z.string(),
  captured_at: z.string(),
  total_percent: z.number().nullable(),
  recommendation: z.string().nullable(),
  scored_count: z.number(),
  criteria_count: z.number(),
});
export type ReadinessSnapshotListItem = z.infer<typeof ReadinessSnapshotListItemSchema>;

export const ReadinessSnapshotListSchema = z.object({
  items: z.array(ReadinessSnapshotListItemSchema),
  next_cursor: z.string().nullable(),
});
export type ReadinessSnapshotList = z.infer<typeof ReadinessSnapshotListSchema>;

export interface ListReadinessSnapshotsOpts {
  cursor?: string;
  limit?: number;
}

export function listReadinessSnapshots(
  engagementId: string,
  opts: ListReadinessSnapshotsOpts = {},
): Promise<ReadinessSnapshotList> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/readiness/snapshots`,
    ReadinessSnapshotListSchema,
    { query: opts as Record<string, string | number | boolean | undefined> },
  );
}

export function useReadinessSnapshotsQuery(
  engagementId: string | undefined,
  opts: ListReadinessSnapshotsOpts = {},
) {
  return useQuery({
    queryKey: ['readiness', 'snapshots', engagementId, opts] as const,
    queryFn: () => listReadinessSnapshots(engagementId!, opts),
    enabled: Boolean(engagementId),
    staleTime: 30_000,
  });
}
